import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useAlertsLive } from '@/hooks/useAlerts'
import { ROUTES } from '@/components/gg/chrome'
import type { Alert, WatchState } from './types'
import '@/styles/alerts.css'

/**
 * The push, in the app (Brian's app.html "Send a test alert" banner, watch
 * spec §10.3: the alert carries the headline). When a match lands while the
 * app is open, a notification slides over the top of the screen; tap it for
 * the alert. The phone's own push comes from breaking-scan either way.
 *
 * Mount once, in the shell (it renders into document.body).
 */
export function AlertPushBanner() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  const nav = useNavigate()
  const [a, setA] = useState<Alert | null>(null)
  const [on, setOn] = useState(false)
  const t = useRef(0)

  useAlertsLive(clientId, (row, kind) => {
    if (kind !== 'INSERT' || !row?.id) return
    const w = qc.getQueryData<WatchState>(['watch-state', clientId])
    if (w && w.push === false) return
    setA(row)
    setOn(false)
    requestAnimationFrame(() => setOn(true))
    window.clearTimeout(t.current)
    t.current = window.setTimeout(() => setOn(false), 6500)
  })
  useEffect(() => () => window.clearTimeout(t.current), [])

  if (!a) return null
  const w = qc.getQueryData<WatchState>(['watch-state', clientId])
  return createPortal(
    <button type="button" className={`gg gga-push${on ? ' on' : ''}`} aria-hidden={!on} tabIndex={on ? 0 : -1}
      onClick={() => { setOn(false); nav(ROUTES.alert(a.id)) }}>
      <div className="h"><img src="/brand/gogrov-icon.svg" alt="" />GoGrov<span className="b">Breaking</span><span className="t">now</span></div>
      <div className="s">{a.subject}<span>· {a.condition}</span></div>
      <div className="m">{a.headline}</div>
      <div className="o">{a.outlet}{w ? ` · alert budget ${Math.max(0, w.bank - 1)} / ${w.cap}` : ''}</div>
    </button>,
    document.body,
  )
}
